/**
 * PageLayout Component
 *
 * Reusable page layout with breadcrumb bar, sticky header, and content area.
 * Extracts the common breadcrumb + header + content pattern used across all pages.
 *
 * KAN-961: внутри кабинета (DashboardLayout, PageHeaderProvider) title,
 * subtitle и крошки уходят в верхнюю строку, белой шапки нет; кнопки
 * (backButton, titleAddon, actions) стоят компактной строкой над содержимым.
 * Без верхней строки (отдельный рендер страницы) шапка рисуется как раньше.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHasPageHeaderSlot, useRegisterPageHeader } from './pageHeaderContext';

/**
 * true: страница отрисована внутри другой страницы (вкладка Настроек),
 * PageLayout отдаёт только действия и содержимое.
 */
const EmbeddedPageContext = createContext(false);

export function EmbeddedPage({ children }: { children: ReactNode }) {
  return <EmbeddedPageContext.Provider value={true}>{children}</EmbeddedPageContext.Provider>;
}

function BackButton({ backButton }: { backButton: { href?: string; onClick?: () => void } }) {
  return backButton.href ? (
    <Link to={backButton.href}>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <ArrowLeft className="h-4 w-4" />
      </Button>
    </Link>
  ) : (
    <Button
      variant="ghost"
      size="icon"
      data-testid="back-button"
      onClick={backButton.onClick}
      className="h-8 w-8"
    >
      <ArrowLeft className="h-4 w-4" />
    </Button>
  );
}

interface BreadcrumbItem {
  label: string;
  href?: string;
  'data-testid'?: string;
}

interface PageLayoutProps {
  title: string;
  subtitle?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  backButton?: { href?: string; onClick?: () => void };
  titleAddon?: ReactNode;
  titleIcon?: ReactNode;
  'data-testid'?: string;
  stickyHeader?: boolean;
  maxWidth?: string;
  contentClassName?: string;
  /** min-h-screen у корня страницы; false - страница высотой по содержимому (KAN-758). */
  fillScreen?: boolean;
  children: ReactNode;
}

export function PageLayout({
  title,
  subtitle,
  breadcrumbs,
  actions,
  backButton,
  titleAddon,
  titleIcon,
  stickyHeader = true,
  maxWidth = 'max-w-7xl',
  contentClassName,
  fillScreen = true,
  children,
  ...props
}: PageLayoutProps) {
  const testId = props['data-testid'];
  const embedded = useContext(EmbeddedPageContext);
  const inTopBar = useHasPageHeaderSlot() && !embedded;
  useRegisterPageHeader({ title, subtitle, breadcrumbs }, inTopBar);

  // Страница внутри вкладки другой страницы (Настройки): без своей шапки,
  // хлебных крошек и min-h-screen; кнопки действий остаются над содержимым.
  if (embedded) {
    return (
      <div data-testid={testId}>
        {(actions || titleAddon) && (
          <div className="mb-4 flex items-center justify-end gap-3">
            {titleAddon}
            {actions}
          </div>
        )}
        {children}
      </div>
    );
  }

  const mainClass = `${maxWidth} mx-auto px-4 sm:px-6 py-4 sm:py-6${contentClassName ? ` ${contentClassName}` : ''}`;

  // KAN-961: заголовок в верхней строке кабинета, здесь только кнопки страницы.
  if (inTopBar) {
    // Отступы уже даёт <main> кабинета: без min-h-screen и своих полей
    // страница не прокручивается впустую, место отдаётся содержимому.
    const hasActionsRow = Boolean(backButton || titleAddon || actions);
    return (
      <div data-testid={testId}>
        <main className={`${maxWidth} mx-auto${contentClassName ? ` ${contentClassName}` : ''}`}>
          {hasActionsRow && (
            <div data-testid="page-actions" className="mb-4 flex flex-wrap items-center gap-3">
              {backButton && <BackButton backButton={backButton} />}
              {titleAddon}
              {actions && <div className="ml-auto">{actions}</div>}
            </div>
          )}
          {children}
        </main>
      </div>
    );
  }

  return (
    <div data-testid={testId} className={`${fillScreen ? 'min-h-screen ' : ''}bg-gray-50`}>
      {/* Combined header: breadcrumb + title in a single white block */}
      <header
        data-testid="page-header"
        className={`bg-white border-b px-4 sm:px-6 shadow-sm${stickyHeader ? ' sticky top-0 z-10' : ''}`}
      >
        {/* Breadcrumb */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div data-testid="breadcrumb" className="pt-3 pb-1">
            <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
              {breadcrumbs.map((item, index) => {
                const isFirst = index === 0;
                return (
                  <span key={index} className="flex items-center">
                    {index > 0 && <ChevronRight className="h-4 w-4 mx-2" />}
                    {item.href ? (
                      <Link
                        to={item.href}
                        className="flex items-center hover:text-gray-700 transition-colors"
                        data-testid={item['data-testid']}
                      >
                        {isFirst && <Home className="h-4 w-4 mr-1" />}
                        {item.label}
                      </Link>
                    ) : (
                      <span
                        className="text-gray-900 font-medium"
                        aria-current="page"
                        data-testid={item['data-testid']}
                      >
                        {isFirst && !breadcrumbs.some((b) => b.href) && (
                          <Home className="h-4 w-4 mr-1 inline-block align-text-bottom text-gray-500" />
                        )}
                        {item.label}
                      </span>
                    )}
                  </span>
                );
              })}
            </nav>
          </div>
        )}

        {/* Title row */}
        <div className={`flex items-center justify-between py-4 ${maxWidth} mx-auto`}>
          <div className="flex items-center gap-3">
            {backButton && <BackButton backButton={backButton} />}
            {titleIcon}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h1>
                {titleAddon}
              </div>
              {subtitle && (
                typeof subtitle === 'string' ? (
                  <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>
                ) : (
                  <div className="mt-0.5 text-sm text-gray-400">{subtitle}</div>
                )
              )}
            </div>
          </div>

          {actions && <div>{actions}</div>}
        </div>
      </header>

      {/* Content */}
      <main className={mainClass}>
        {children}
      </main>
    </div>
  );
}
